import { SaveContainerDTO, SaveContainerDropDTO } from 'business/models/Models';
import { getRandomNegativeNumber } from '../Imaging/utils';
import { defaultSaveContainerDropDTO } from './constants';
import { RowInfoType } from 'components/customComponents/Controls/RMTable';
import { PlateTypeEnum, PlateTypesRowInfo } from './types';
import isEqual from 'lodash.isequal';
import { MaximumPlateWells } from 'components/Experiment/constants';

export interface PlateTypeCountErrorMsg {
  isCountValid?: boolean;
  msg?: string;
}
export const checkPlateTypesCount = (
  allPlateTypes: SaveContainerDTO[],
): PlateTypeCountErrorMsg => {
  if (
    !allPlateTypes.some(
      (plateType) =>
        plateType.type === PlateTypeEnum['Screen Lot Plate'] && plateType.enable === true,
    )
  ) {
    return {
      isCountValid: false,
      msg: 'At least one Screen lot type container should be enabled.',
    };
  } else if (
    !allPlateTypes.some(
      (plateType) =>
        plateType.type === PlateTypeEnum['Experiment Plate'] && plateType.enable === true,
    )
  ) {
    return {
      isCountValid: false,
      msg: 'At least one Experiment lot type container should be enabled.',
    };
  }
  return {
    msg: '',
    isCountValid: true,
  };
};

const excludedKeys = ['containerDrops', 'defaultFragmentVolume'];

export const validatePlateTypes = (
  plateType: SaveContainerDTO,
  allPlateTypes: SaveContainerDTO[],
  index: number,
  originalPlate?: SaveContainerDTO,
): PlateTypesRowInfo => {
  let msg = '';
  const attributesToCheck = Object.keys(plateType).filter(
    (key) => !excludedKeys.includes(key),
  );
  for (const property of attributesToCheck) {
    if (plateType[property] === undefined || plateType[property] === null) {
      msg += `${property} cannot be empty.\n`;
      return {
        [index]: {
          msg: msg,
          type: RowInfoType.Error,
        },
      };
    } else if (plateType[property].toString().length === 0) {
      msg += `${property} cannot be empty.\n`;
      return {
        [index]: {
          msg: msg,
          type: RowInfoType.Error,
        },
      };
    }
  }
  if (allPlateTypes.find((x) => x.name === plateType.name && x.id !== plateType.id)) {
    msg += 'Name must be unique.\n';
  }
  if (Number(plateType.numRows) < 1) {
    msg += 'Rows must be greater than or equal to 1.\n';
  }
  if (Number(plateType.numColumns) < 1) {
    msg += 'Columns must be greater than or equal to 1.\n';
  }
  if (Number(plateType.maxWellVolume) < 1) {
    msg += 'MaxWellVolume must be greater than 0.\n';
  }
  if (
    plateType.inUse &&
    Number(plateType.maxWellVolume < Number(originalPlate?.maxWellVolume))
  ) {
    msg += `Max Well Volume lower than previous value.\n`;
  }
  if (
    plateType.inUse &&
    Number(plateType.maxDropVolume < Number(originalPlate?.maxDropVolume))
  ) {
    msg += `Max Drop Volume lower than previous value.\n`;
  }
  if (Number(plateType.maxNumDrops) > 10) {
    msg += 'MaxNumDrops must be less than or equal to 10.\n';
  }
  if (
    plateType.inUse &&
    Number(plateType.maxNumDrops) < Number(originalPlate?.maxNumDrops)
  ) {
    msg += `Drop number lower than previous value.\n`;
  }
  if (Number(plateType.maxWellVolume) < Number(plateType.defaultWellVolume)) {
    msg += `DefaultWellVolume must be greater than or equal to 0 and less than or equal to ${plateType.maxWellVolume.toString()}`;
  }
  if (Number(plateType.maxDropVolume) < Number(plateType.defaultDropVolume)) {
    msg += `DefaultDropVolume must be greater than or equal to 0 and less than or equal to ${plateType.maxDropVolume.toString()}`;
  }
  if (
    Number(plateType.maxNumDrops) < 1 &&
    plateType.type !== PlateTypeEnum['Screen Lot Plate']
  ) {
    msg += "'MaxNumDrops' must be greater than or equal to 1.\n";
  }
  if (plateType.isAtImager && !plateType.enable) {
    msg += 'Plate type cannot be disabled when registered in an Imager.\n';
  }
  if (plateType.numColumns * plateType.numRows > MaximumPlateWells) {
    msg += `Rows x Columns must not exceed ${MaximumPlateWells}.\n`;
  }

  if (msg) {
    return {
      [index]: {
        msg: msg,
        type: RowInfoType.Error,
      },
    };
  }
  return {};
};

export const createNewContainerDto = () => {
  const newContainerDto = new SaveContainerDTO();
  Object.assign(newContainerDto, defaultSaveContainerDropDTO);
  newContainerDto.containerDrops = [createNewDrop(1, newContainerDto.id)];
  return newContainerDto;
};

export const createNewDrop = (dropNumber, containerId) => {
  const newDrop = new SaveContainerDropDTO();
  newDrop.id = getRandomNegativeNumber();
  newDrop.containerId = containerId;
  newDrop.dropNumber = dropNumber;
  return newDrop;
};

export const getModifiedContainers = (
  currentData: SaveContainerDTO[],
  originalData: SaveContainerDTO[],
) => {
  const originalMap = new Map(originalData.map((item) => [item.id, item]));

  return currentData.filter((current) => {
    const original = originalMap.get(current.id);
    if (!original) {
      return true;
    }

    return !isEqual(current, original);
  });
};
